import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });

  React.useEffect(() => {
    try {
      const reason = sessionStorage.getItem('logout_reason');
      if (reason) {
        toast.info(reason, { duration: 9000 });
        sessionStorage.removeItem('logout_reason');
      }
    } catch (e) {}
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Check if password change is required
      if (response.data.force_password_change) {
        setTempToken(response.data.token);
        setTempUser(response.data.user);
        setShowPasswordChangeDialog(true);
        toast.info('Jy moet jou wagwoord verander voor jy kan voortgaan');
      } else {
        login(response.data.token, response.data.user);
        toast.success('Welkom terug!');
        
        // Redirect based on role
        if (response.data.user.role === 'writer') {
          navigate('/writer');
        } else if (response.data.user.role === 'proofreader') {
          navigate('/proofreader');
        } else if (response.data.user.role === 'translator') {
          navigate('/translator');
        } else if (response.data.user.role === 'admin') {
          navigate('/admin');
        }
      }
    } catch (error) {
      // Surface the most informative message we can — helps diagnose login issues
      // (wrong password vs. account status vs. backend down vs. CORS).
      let msg = error.response?.data?.detail;
      if (!msg) {
        if (error.response?.status) {
          msg = `Aanmelding het misluk (HTTP ${error.response.status}). Probeer asseblief weer of kontak ondersteuning.`;
        } else if (error.request) {
          msg = 'Aanmelding het misluk — geen antwoord van bediener nie. Kontroleer jou internetverbinding.';
        } else {
          msg = `Aanmelding het misluk: ${error.message || 'onbekende fout'}`;
        }
      }
      toast.error(msg, { duration: 8000 });
      // Also dump full error to console for the user to copy when reporting
      console.error('[login] failed:', {
        status: error.response?.status,
        detail: error.response?.data,
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!newPassword || !confirmPassword) {
      toast.error('Vul asseblief beide wagwoord velde in');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Wagwoord moet ten minste 8 karakters lank wees');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Wagwoorde stem nie ooreen nie');
      return;
    }

    setChangingPassword(true);
    try {
      // Call backend to change password
      await axios.put(
        `${API}/auth/change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      toast.success('Wagwoord suksesvol verander!');
      
      // Now log in with the new credentials
      login(tempToken, tempUser);
      setShowPasswordChangeDialog(false);
      
      // Redirect based on role
      if (tempUser.role === 'writer') {
        navigate('/writer');
      } else if (tempUser.role === 'proofreader') {
        navigate('/proofreader');
      } else if (tempUser.role === 'admin') {
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie wagwoord verander nie');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link 
            to="/" 
            className="inline-block mb-4 text-orange-600 hover:text-orange-700 transition-colors"
          >
            ← Terug na Tuisblad
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Meld Aan
          </h1>
          <p className="text-gray-600">Welkom terug by DIE BIBLIOTEEK</p>
        </div>

        <Card className="border-2 border-orange-200 shadow-xl">
          <CardHeader>
            <CardTitle>Aanmelding</CardTitle>
            <CardDescription>Voer jou e-pos en wagwoord in</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">E-pos</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="password">Wagwoord</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={loading}
              >
                {loading ? 'Besig...' : 'Meld Aan'}
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-gray-600">
                Nog nie geregistreer nie?{' '}
                <Link to="/register" className="text-orange-600 hover:text-orange-700 font-semibold">
                  Registreer nou
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Force Password Change Dialog */}
      <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Verander Jou Wagwoord
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Jy moet jou wagwoord verander voor jy die platform kan gebruik. Kies 'n sterk wagwoord met ten minste 8 karakters.
            </p>
            
            <div>
              <Label htmlFor="new-password">Nuwe Wagwoord</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Voer nuwe wagwoord in"
              />
            </div>

            <div>
              <Label htmlFor="confirm-password">Bevestig Wagwoord</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Bevestig nuwe wagwoord"
              />
            </div>

            <Button 
              onClick={handlePasswordChange}
              disabled={changingPassword || !newPassword || !confirmPassword}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              {changingPassword ? 'Besig met verander...' : 'Verander Wagwoord'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;